import mongoose from "mongoose";

import { z } from 'zod';
import type { RefinementCtx } from "zod";


/**
 * Validates if a given string matches MongoDB's ObjectID format.
 * 
 * @param fieldName - Name of the field being validated. Used for custom error messages.
 * @returns A Zod schema instance for MongoDB ObjectID validation.
 * 
 * @example
 * const userId = mongoId('User ID');
 * userId.parse('your-input-here');
 * 
 * TODO: Handle different refs 
 */
const mongoId = (fieldName: string) => {
    return z.string({ required_error: `${fieldName} is required` })
        .length(24, "An ID must be 24 characters long.")
        .regex(/^[a-fA-F0-9]+$/, "Must be a valid hexadecimal value.")
        .refine(value => parseInt(
            value.slice(0, 8), 16) < parseInt(
                Math.floor(Date.now() / 1000).toString(16), 16), {
            message: "ID is set in the future."
        })
        .superRefine((val: string, ctx: RefinementCtx) => {
            if (mongoose.Types.ObjectId.isValid(val)) return true;

            ctx.addIssue({
                code: "custom",
                message: `${ctx.path.join(".")} must be a valid MongoDB ObjectID.`,
            });
        });
};

/**
 * Zod schema for MongoDB ObjectID validation without custom error messages.
 * If you require custom validation error messages, use {@link mongoId} function.
 * 
 * @example
 * mongoIdSchema.parse('your-input-here');
 */
const mongoIdSchema = z.string()
    .length(24, "An ID must be 24 characters long.")
    .regex(/^[a-fA-F0-9]+$/, "Must be a valid hexadecimal value.")
    .refine(value => parseInt(value.slice(0, 8), 16) < parseInt(Math.floor(Date.now() / 1000).toString(16), 16), {
        message: "ID is set in the future."
    })
    .superRefine((val: string, ctx: RefinementCtx) => {
        if (mongoose.Types.ObjectId.isValid(val)) return true;

        ctx.addIssue({
            code: "custom",
            message: `${ctx.path.join(".")} must be a valid MongoDB ObjectID.`,
        });
    });


export { mongoIdSchema, mongoId };
